import { useId, useState, type ReactNode } from 'react';
import { ApiError, Button, HelpTip, Input, cn } from '@dataroom/shared';
import type { InstantiateChecklistResponse, SavedChecklistTemplate } from '@dataroom/shared';
import {
  useInstantiateChecklist,
  useInstantiateChecklistXlsx,
  useSavedChecklists,
  useTenantPlan,
} from '@/api/hooks';
import {
  checklistCountsLabel,
  checklistProblemsOf,
  checklistXlsxBody,
  closingVaultLocked,
  isForbidden,
  nameFromFilename,
  PLUS_PLANS_BADGE,
  PROFESSIONAL_ONLY_HINT,
  savedChecklistsLocked,
  planChecklistSetup,
  savedChecklistLabel,
  TRIAL_CHECKLIST_MESSAGE,
  type ChecklistSource,
} from '@/lib/checklist';
import { fileToBase64 } from '@/lib/file';
import { ChecklistProblemsTable } from './ChecklistProblemsTable';
import { ChecklistSetupProgress } from './ChecklistSetupProgress';
import { ChecklistTemplateLinks } from './ChecklistTemplateLinks';
import { Modal } from './Modal';

interface ChecklistSourceDialogProps {
  roomId: string;
  onClose: () => void;
}

interface Done {
  summary: InstantiateChecklistResponse;
  savedAs: string | null;
}

/**
 * "Set up the due-diligence checklist" (ADR-0017) — opened right after a room is
 * created, and from the room's Checklist tab while it has no phases. Five
 * sources: the ClosingVault checklist, the standard checklist, an uploaded .xlsx
 * (optionally saved for reuse), a tenant saved checklist, or skip. The saved-
 * checklist option (and saving an upload) is tenant-admin only — on a 403 from GET
 * /dd-templates those affordances simply don't render. The ClosingVault checklist
 * is a Plus and Professional feature (ADR-0044, amended 2026-10-26): there it is
 * "Recommended" and preselected; on a FREE TRIAL or Starter it is shown locked
 * ("Included with Plus and Professional"), the standard checklist is the
 * preselected choice, and "Upload your checklist" offers the sample layout. A
 * rejected workbook (422) keeps the dialog open with its row-level problems so the
 * user can fix and re-upload.
 */
export function ChecklistSourceDialog({ roomId, onClose }: ChecklistSourceDialogProps) {
  const saved = useSavedChecklists();
  const instantiate = useInstantiateChecklist(roomId);
  const instantiateXlsx = useInstantiateChecklistXlsx(roomId);

  const plan = useTenantPlan();
  // Free trial and Starter (ADR-0044): the standard checklist is the default, the
  // ClosingVault one is locked. A plan read that fails (e.g. a non-admin's 403) is
  // treated as unlocked; the server decides.
  const trial = plan.data?.isTrial === true;
  const locked = closingVaultLocked(plan.data);
  // Saving a checklist for reuse is Professional only; existing saved ones stay usable.
  const savedLocked = savedChecklistsLocked(plan.data);
  // null = not chosen yet: preselect the standard on a locked plan, the ClosingVault otherwise.
  const [chosen, setSource] = useState<ChecklistSource | null>(null);
  const source: ChecklistSource =
    chosen === 'default' && locked ? 'standard' : (chosen ?? (locked ? 'standard' : 'default'));
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [saveForReuse, setSaveForReuse] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [encoding, setEncoding] = useState(false);
  const [done, setDone] = useState<Done | null>(null);
  /** When the current setup started (drives the progress panel's clock). */
  const [startedAt, setStartedAt] = useState(0);
  const fileInputId = useId();
  const saveNameId = useId();
  const savedSelectId = useId();

  const tenantAdmin = saved.isSuccess;
  const savedList: SavedChecklistTemplate[] = saved.data ?? [];
  // Shown to every tenant admin so the feature is discoverable; disabled (with a
  // hint) until the firm has saved at least one checklist.
  const showSaved = tenantAdmin && !isForbidden(saved.error);
  // Only a valid saved checklist can seed a room (invalid ones are listed, disabled).
  const hasSaved = savedList.some((t) => t.valid !== false);
  const selectedTemplateId = templateId || savedList.find((t) => t.valid !== false)?.id || '';

  const pending = instantiate.isPending || instantiateXlsx.isPending || encoding;
  const activeError = source === 'upload' ? instantiateXlsx.error : instantiate.error;
  const problems = source === 'upload' ? checklistProblemsOf(instantiateXlsx.error) : null;
  const plainError =
    fileError ??
    (problems
      ? null
      : activeError instanceof ApiError
        ? activeError.message
        : activeError
          ? 'Something went wrong. Please try again.'
          : null);

  function choose(next: ChecklistSource) {
    setSource(next);
    setFileError(null);
    instantiate.reset();
    instantiateXlsx.reset();
  }

  function pickFile(f: File | null) {
    instantiateXlsx.reset();
    setFileError(null);
    // Follow the picked file's name unless the user typed their own.
    const prevAuto = file ? nameFromFilename(file.name) : '';
    if (f && (!saveName.trim() || saveName === prevAuto)) setSaveName(nameFromFilename(f.name));
    setFile(f);
  }

  async function submit() {
    setFileError(null);
    const plan = planChecklistSetup({
      source,
      file,
      savedTemplateId: selectedTemplateId,
      saveForReuse: saveForReuse && !savedLocked,
      saveName,
      tenantAdmin,
      locked,
    });
    if (plan.kind === 'close') return onClose();
    if (plan.kind === 'invalid') return setFileError(plan.message);
    setStartedAt(Date.now());
    if (plan.kind === 'instantiate') {
      instantiate.mutate(plan.body, {
        onSuccess: (summary) => setDone({ summary, savedAs: null }),
      });
      return;
    }
    setEncoding(true);
    let contentBase64: string;
    try {
      contentBase64 = await fileToBase64(plan.file);
    } catch {
      setFileError("That file couldn't be read. Please choose it again.");
      return;
    } finally {
      setEncoding(false);
    }
    instantiateXlsx.mutate(checklistXlsxBody(plan.file, contentBase64, plan.saveAs), {
      onSuccess: (res) => setDone({ summary: res, savedAs: res.savedTemplate?.name ?? null }),
    });
  }

  if (done) {
    return (
      <Modal
        title="Checklist ready"
        description="The checklist is set up. On the Checklist tab you can release phases, set their dates, rename them, and add, edit or remove items."
        onClose={onClose}
        footer={
          <>
            <span />
            <Button onClick={onClose}>Done</Button>
          </>
        }
      >
        <div className="space-y-2 text-sm" role="status">
          <p className="font-medium">
            Created{' '}
            {checklistCountsLabel({
              phaseCount: done.summary.phasesCreated,
              categoryCount: done.summary.categoriesCreated,
              requestCount: done.summary.requestsCreated,
              questionCount: done.summary.questionsCreated,
            })}
            .
          </p>
          {done.savedAs ? (
            <p className="text-muted-foreground">
              Saved as “{done.savedAs}” — pick it under “Use a saved checklist” next time.
            </p>
          ) : null}
        </div>
      </Modal>
    );
  }

  const submitLabel =
    source === 'skip'
      ? 'Skip for now'
      : source === 'upload'
        ? pending
          ? 'Uploading…'
          : 'Upload and set up'
        : pending
          ? 'Setting up…'
          : source === 'saved'
            ? 'Use saved checklist'
            : source === 'standard'
              ? 'Use the standard checklist'
              : 'Use the ClosingVault checklist';

  return (
    <Modal
      title="Set up the due-diligence checklist"
      headerAside={<HelpTip id="admin.checklist-source.title" />}
      description="Choose where this room's requested-documents checklist comes from. You can add, edit, or remove items afterwards."
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={pending || (source === 'saved' && !selectedTemplateId)}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      {pending ? (
        <ChecklistSetupProgress
          stage={encoding ? 'reading' : 'building'}
          startedAt={startedAt}
          upload={source === 'upload'}
          expecting={
            source === 'saved'
              ? (() => {
                  const t = savedList.find((x) => x.id === selectedTemplateId);
                  return t
                    ? checklistCountsLabel({ phaseCount: t.phaseCount, requestCount: t.requestCount })
                    : undefined;
                })()
              : undefined
          }
        />
      ) : null}
      {/* Hidden (not unmounted) while setting up, so a picked file and the
          choices survive a rejected workbook. */}
      <fieldset className={cn('space-y-3', pending && 'hidden')} disabled={pending}>
        <legend className="sr-only">Checklist source</legend>

        {trial ? (
          <p className="rounded-md bg-brass-soft px-3 py-2 text-xs text-ink">
            {TRIAL_CHECKLIST_MESSAGE}
          </p>
        ) : null}

        <SourceOption
          value="default"
          current={source}
          onChoose={choose}
          title="ClosingVault checklist"
          badge={locked ? PLUS_PLANS_BADGE : 'Recommended'}
          disabled={locked}
          hint="Our tested, thorough checklist: phases, nested categories, financial requests stamped with real years and months, and phase questions."
        />

        <SourceOption
          value="standard"
          current={source}
          onChoose={choose}
          title="Standard checklist"
          badge={locked ? 'Default' : undefined}
          hint="Our standard due-diligence checklist: financials, legal, commercial and operations, HR, risk and technology, with phase questions. Add, edit or remove items afterwards, or download it as a spreadsheet to see how a checklist is laid out."
        >
          <ChecklistTemplateLinks showDefault={false} showSample />
        </SourceOption>

        <SourceOption
          value="upload"
          current={source}
          onChoose={choose}
          title={locked ? 'Upload your checklist' : 'Upload your checklist (.xlsx)'}
          hint={
            locked
              ? 'Build your list in Excel — start from the sample layout, or adapt your firm’s own — and upload the .xlsx.'
              : 'Fill in our Excel template — or start from the ClosingVault checklist — and upload it.'
          }
        >
          <div className="space-y-3">
            <ChecklistTemplateLinks showDefault={tenantAdmin && !locked} showSample={locked} />
            <div className="space-y-1.5">
              <label htmlFor={fileInputId} className="text-sm font-medium">
                Checklist file
              </label>
              <input
                id={fileInputId}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {tenantAdmin ? (
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <label
                    className={cn('flex items-center gap-2 text-sm', savedLocked && 'opacity-60')}
                  >
                    <input
                      type="checkbox"
                      checked={saveForReuse && !savedLocked}
                      disabled={savedLocked}
                      onChange={(e) => setSaveForReuse(e.target.checked)}
                    />
                    Save this checklist for reuse
                    {savedLocked ? (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {PROFESSIONAL_ONLY_HINT}
                      </span>
                    ) : null}
                  </label>
                  <HelpTip id="admin.checklist-source.save-for-reuse" />
                </div>
                {saveForReuse && !savedLocked ? (
                  <div className="space-y-1">
                    <label htmlFor={saveNameId} className="text-xs text-muted-foreground">
                      Save as
                    </label>
                    <Input
                      id={saveNameId}
                      value={saveName}
                      maxLength={120}
                      placeholder="e.g. Lower-middle-market services"
                      onChange={(e) => setSaveName(e.target.value)}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {problems ? <ChecklistProblemsTable {...problems} /> : null}
          </div>
        </SourceOption>

        {showSaved ? (
          <SourceOption
            value="saved"
            current={source}
            onChoose={choose}
            title="Use a saved checklist"
            disabled={!hasSaved}
            badge={!hasSaved && savedLocked ? PROFESSIONAL_ONLY_HINT : undefined}
            hint={
              hasSaved
                ? `A checklist your firm saved earlier (${savedList.length} saved).`
                : savedLocked
                  ? 'Save a checklist once, then start every new deal from it.'
                  : 'None saved yet. Upload a checklist and tick “Save this checklist for reuse”, or add one on the Checklists page.'
            }
          >
            <div className="space-y-1">
              <label htmlFor={savedSelectId} className="text-xs text-muted-foreground">
                Saved checklist
              </label>
              <select
                id={savedSelectId}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={selectedTemplateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                {savedList.map((t) => (
                  // An invalid saved checklist can never seed a room (the API
                  // refuses it): listed for visibility, not selectable.
                  <option key={t.id} value={t.id} disabled={t.valid === false}>
                    {t.valid === false
                      ? `${t.name} — can’t be used: ${t.invalidReason ?? 'invalid checklist'}`
                      : savedChecklistLabel(t)}
                  </option>
                ))}
              </select>
            </div>
          </SourceOption>
        ) : null}

        <SourceOption
          value="skip"
          current={source}
          onChoose={choose}
          title="Skip for now"
          hint="Leave the checklist empty and set it up later from the room's Checklist tab."
        />
      </fieldset>

      {plainError ? (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {plainError}
        </p>
      ) : null}
    </Modal>
  );
}

/** One radio choice, rendered as a selectable panel; `children` (the choice's own
 * fields) only show while it is selected. */
function SourceOption({
  value,
  current,
  onChoose,
  title,
  hint,
  badge,
  disabled = false,
  children,
}: {
  value: ChecklistSource;
  current: ChecklistSource;
  onChoose: (v: ChecklistSource) => void;
  title: string;
  hint: string;
  badge?: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  const id = useId();
  const selected = current === value;
  return (
    <div
      className={cn(
        'rounded-lg border p-3 transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-input',
        disabled && 'opacity-60',
      )}
    >
      <label
        htmlFor={id}
        className={cn('flex items-start gap-3', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
      >
        <input
          id={id}
          type="radio"
          name="checklist-source"
          value={value}
          checked={selected}
          disabled={disabled}
          onChange={() => onChoose(value)}
          className="mt-1"
        />
        <span className="space-y-0.5">
          <span className="flex items-center gap-2 text-sm font-medium">
            {title}
            {badge ? (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {badge}
              </span>
            ) : null}
          </span>
          <span className="block text-xs text-muted-foreground">{hint}</span>
        </span>
      </label>
      {/* Kept mounted while unselected so a picked file survives switching. */}
      {children ? <div className={selected ? 'mt-3 pl-7' : 'hidden'}>{children}</div> : null}
    </div>
  );
}
